//#region Imports

import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { InstanceProxy } from '../../models/proxies/instance.proxy';
import { HttpAsyncService } from '../../modules/http-async/services/http-async.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { RefreshTokenInterceptor } from '../../modules/http-async/interceptors/refresh-token-interceptor.service';

//#endregion
@Injectable({
  providedIn: 'root',
})
export class InstanceService {

  //#region Constructor

  constructor(
    protected readonly router: Router,
    protected readonly http: HttpAsyncService,
    protected readonly title: Title,
  ) {
  }

  //#endregion

  //#region Private Properties

  private currentInstanceSubject: BehaviorSubject<InstanceProxy | null> = new BehaviorSubject<InstanceProxy | null>(null);

  public get currentInstance(): InstanceProxy | null {
    return this.currentInstanceSubject.value;
  }
  
  public getCurrentInstance$(): Observable<InstanceProxy | null> {
    return this.currentInstanceSubject.asObservable();
  }

  //#endregion

  //#region Public Methods

  public async initialize(): Promise<void> {
    const subdomain = this.getCurrentSubdomain();

    await this.loadInstanceBySubdomain(subdomain);

    (window as any).__uniGetInstance = () => this.currentInstanceSubject;
    (window as any).__uniSetCurrentInstanceBySubdomain = (domain: string) => this.loadInstanceBySubdomain(domain);
    (window as any).__uniClearForcedSubdomain = () => localStorage.removeItem(environment.keys.forceSubdomain);
    (window as any).__uniForceSubdomain = (domain: string) => {
      localStorage.setItem(environment.keys.forceSubdomain, domain);
      this.loadInstanceBySubdomain(domain);
    };
  }

  public getCurrentSubdomain(): string {
    const overrideSubdomain = localStorage.getItem(environment.keys.forceSubdomain);

    if (overrideSubdomain)
      return overrideSubdomain;

    const url = location.hostname;

    if (url === 'localhost')
      return environment.apiBaseUrl.split('.')[0].split('/').slice(-1)[0];

    const part: string = url.split('.')[0];

    if (part === 'dev' || part === 'qas' || part === 'staging')
      return url.split('.')[1];
    
    return url.split('.')[0] || 'localhost';
  }

  //#endregion

  //#region Private Methods

  private async loadInstanceBySubdomain(subdomain: string): Promise<void> { 
    const { error, success } = await this.http.get<InstanceProxy>(
      `/instances/${ subdomain }`, 
      { headers: { 
        [RefreshTokenInterceptor.DISABLE_HEADER] : 'true'
      }}
    );
    
    if (error || !success)
      return void await this.router.navigateByUrl('/instance-not-found');

    this.currentInstanceSubject.next(success);

    this.setupInstance(success);
  }

  private setupInstance(instance: InstanceProxy): void {
    this.title.setTitle(instance.fantasyName);

    const faviconIcon: HTMLLinkElement | null = document.querySelector('link[rel="icon"]');

    if (!faviconIcon)
      return;

    faviconIcon.href = instance.faviconUrl;
  }

  //#endregion

}
